/**
 * A screenshot in Clu chat: the composer takes one PNG, JPEG or
 * WebP image, scales it to at most 1568 px on the long edge and re-encodes it
 * as PNG (when that stays small) or JPEG. The API reads PNG and JPEG up to
 * 1.5 MB and 2000 px a side; nothing larger leaves the browser.
 */
import type {
  AttachmentAdapter,
  CompleteAttachment,
  PendingAttachment,
} from '@assistant-ui/react';
import {
  browserImageCodec,
  prepareImage,
  type ImageCodec,
  type ImageLimits,
  type ImageType,
} from '@/lib/prepare-image';

export const SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const SCREENSHOT_ACCEPT = SCREENSHOT_TYPES.join(',');
export const SCREENSHOT_MAX_EDGE = 1568;
export const SCREENSHOT_MAX_RAW_BYTES = 20 * 1024 * 1024;
export const SCREENSHOT_PNG_MAX_BYTES = 1.2 * 1024 * 1024;
export const SCREENSHOT_MAX_BYTES = 1.5 * 1024 * 1024;
export const SCREENSHOT_JPEG_QUALITY = 0.85;

export const SCREENSHOT_TOO_LARGE = 'That screenshot is too large.';
export const SCREENSHOT_WRONG_TYPE = 'Screenshots can be PNG, JPEG or WebP.';

/** The first screenshot-type image on the clipboard, if any. */
export function clipboardScreenshot(data: DataTransfer | null): File | null {
  if (!data) return null;
  const fromFiles = Array.from(data.files ?? []).find((f) =>
    SCREENSHOT_TYPES.includes(f.type)
  );
  if (fromFiles) return fromFiles;
  for (const item of Array.from(data.items ?? [])) {
    if (item.kind !== 'file' || !SCREENSHOT_TYPES.includes(item.type)) continue;
    const file = item.getAsFile();
    if (file) return file;
  }
  return null;
}

/** The image a chat turn carries, as the API's `image` field takes it. */
export type ChatImage = {
  mediaType: ImageType;
  /** Base64, no `data:` prefix. */
  data: string;
};

/** Refused before anything is staged; the message is for the user. */
export { ImageRefusedError as ScreenshotRefusedError } from '@/lib/prepare-image';

export type ScreenshotCodec = ImageCodec;

const SCREENSHOT_LIMITS: ImageLimits = {
  types: SCREENSHOT_TYPES,
  maxRawBytes: SCREENSHOT_MAX_RAW_BYTES,
  maxEdge: SCREENSHOT_MAX_EDGE,
  pngMaxBytes: SCREENSHOT_PNG_MAX_BYTES,
  maxBytes: SCREENSHOT_MAX_BYTES,
  jpegQuality: SCREENSHOT_JPEG_QUALITY,
  fallbackName: 'screenshot',
  tooLarge: SCREENSHOT_TOO_LARGE,
  wrongType: SCREENSHOT_WRONG_TYPE,
};

/**
 * The file the chat sends in place of `file`: scaled and re-encoded, PNG when
 * that is at most 1.2 MB, else JPEG at 0.85. Refused past 1.5 MB.
 */
export function prepareScreenshot(
  file: File,
  codec: ScreenshotCodec = browserImageCodec
): Promise<File> {
  return prepareImage(file, SCREENSHOT_LIMITS, codec);
}

const readDataUrl = (file: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/** assistant-ui's attachment adapter for one screenshot per message. */
export function createScreenshotAttachmentAdapter(
  codec: ScreenshotCodec = browserImageCodec
): AttachmentAdapter {
  return {
    accept: SCREENSHOT_ACCEPT,
    async add({ file }): Promise<PendingAttachment> {
      const prepared = await prepareScreenshot(file, codec);
      return {
        id: `screenshot-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type: 'image',
        name: prepared.name,
        contentType: prepared.type,
        file: prepared,
        status: { type: 'requires-action', reason: 'composer-send' },
      };
    },
    async send(attachment): Promise<CompleteAttachment> {
      return {
        ...attachment,
        status: { type: 'complete' },
        content: [{ type: 'image', image: await readDataUrl(attachment.file) }],
      };
    },
    async remove() {},
  };
}

const DATA_URL = /^data:(image\/(?:png|jpeg));base64,(.+)$/;

/** The API's image from a `data:` URL; null for anything it does not take. */
export function chatImageFromDataUrl(url: string): ChatImage | null {
  const match = DATA_URL.exec(url);
  if (!match) return null;
  return { mediaType: match[1] as ChatImage['mediaType'], data: match[2] };
}

type MessageWithAttachments = {
  content: readonly { type: string; image?: string }[];
  attachments?: readonly { content?: readonly { type: string }[] }[];
};

/** The first image a sent message carries, from its attachments or content. */
export function firstChatImage(message: MessageWithAttachments) {
  const parts = [
    ...(message.attachments ?? []).flatMap((a) => a.content ?? []),
    ...message.content,
  ];
  for (const part of parts) {
    if (part.type !== 'image') continue;
    const image = (part as { image?: unknown }).image;
    if (typeof image !== 'string') continue;
    const parsed = chatImageFromDataUrl(image);
    if (parsed) return parsed;
  }
  return null;
}
